import { Check, TriangleAlert } from "lucide-react";

import styles from "./integrations.module.css";

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

export type StepperState = "done" | "now" | "todo" | "warn" | "blocked";

export type StepperItem = {
  key: string;
  label: string;
  state: StepperState;
  /** A few words under the label: "3 need a value". */
  note?: string;
  /** Why a blocked step cannot be opened yet. */
  why?: string | null;
};

const STATE: Record<StepperState, { className: string; word: string }> = {
  done: { className: styles.stepperDone, word: "done" },
  now: { className: styles.stepperNow, word: "you are here" },
  todo: { className: "", word: "not done yet" },
  warn: { className: styles.stepperWarn, word: "needs work" },
  blocked: { className: styles.stepperBlocked, word: "finish the steps before it first" },
};

/**
 * Numbered steps joined by a line, each done, current, needing work, or not
 * open yet — shown by an icon and a hidden word as well as colour.
 *
 * With `progress`, a line above says which step this is and how many are done.
 */
export function Stepper({
  items,
  onGo,
  label,
  progress,
}: {
  items: StepperItem[];
  onGo: (key: string) => void;
  label: string;
  progress?: { done: number };
}) {
  const at = items.findIndex((item) => item.state === "now" || (item.state === "warn" && false));
  const current = items.find((item) => item.state === "now");
  return (
    <div className={styles.stepperBox}>
      {progress && (
        <div className={styles.stepperTop}>
          <span>
            Step {at + 1} of {items.length}
            {current ? (
              <>
                : <strong>{current.label}</strong>
              </>
            ) : null}
          </span>
          <span className={styles.progress} aria-hidden>
            <span className={styles.progressFill} style={{ width: `${(Math.min(progress.done, items.length) / items.length) * 100}%` }} />
          </span>
          <span className={styles.faint}>
            {Math.min(progress.done, items.length)} of {items.length} done
          </span>
        </div>
      )}
      <ol className={styles.stepper} aria-label={label}>
        {items.map((item, index) => (
          <li key={item.key} className={cx(styles.stepperItem, index > 0 && items[index - 1].state === "done" && styles.stepperLineDone)}>
            <button
              type="button"
              className={cx(styles.stepperButton, STATE[item.state].className)}
              aria-current={item.state === "now" ? "step" : undefined}
              disabled={item.state === "blocked"}
              title={item.state === "blocked" ? (item.why ?? "Finish the steps before this one first.") : undefined}
              onClick={() => onGo(item.key)}
            >
              <span className={styles.stepperDot}>
                {item.state === "done" ? (
                  <Check size={16} aria-hidden />
                ) : item.state === "warn" ? (
                  <TriangleAlert size={15} aria-hidden />
                ) : (
                  index + 1
                )}
              </span>
              <span className={styles.stepperLabel}>{item.label}</span>
              {item.note && <span className={styles.stepperNote}>{item.note}</span>}
              <span className={styles.srOnly}>({STATE[item.state].word})</span>
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
